import React, { useMemo, useState, useRef, useEffect } from 'react';
import useAPI from '../hooks/useAPI';
import { getRowLengthCorrelation } from '../services/api';

/**
 * Analytics page — Row Length vs Faults correlation (India robots).
 * Scatter plot: X = row length (panels), Y = critical+high faults.
 */

const DAYS_OPTIONS = [30, 60, 90, 180, 365];

export default function Analytics() {
  const [days, setDays] = useState(90);
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const [tooltip, setTooltip] = useState({ show: false, x: 0, y: 0, data: null });
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  const { data, loading, error } = useAPI(() => getRowLengthCorrelation(days), [days]);

  const points = data?.data || [];
  const stats = data?.stats || {};

  // Chart dimensions
  const CHART_W = 900;
  const CHART_H = 500;
  const PAD = { top: 30, right: 40, bottom: 60, left: 70 };
  const plotW = CHART_W - PAD.left - PAD.right;
  const plotH = CHART_H - PAD.top - PAD.bottom;

  // Scales
  const { xMax, yMax, xScale, yScale } = useMemo(() => {
    if (!points.length) return { xMax: 100, yMax: 10, xScale: () => 0, yScale: () => 0 };
    const xM = Math.ceil((stats.max_row_length || 100) * 1.05);
    const yM = Math.ceil((stats.max_faults || 10) * 1.1);
    return {
      xMax: xM,
      yMax: yM,
      xScale: (v) => PAD.left + (v / xM) * plotW,
      yScale: (v) => PAD.top + plotH - (v / yM) * plotH,
    };
  }, [points, stats, plotW, plotH]);

  // Draw canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !points.length) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    canvas.width = CHART_W * dpr;
    canvas.height = CHART_H * dpr;
    ctx.scale(dpr, dpr);
    canvas.style.width = CHART_W + 'px';
    canvas.style.height = CHART_H + 'px';

    // Clear
    ctx.clearRect(0, 0, CHART_W, CHART_H);

    // Background
    ctx.fillStyle = '#fff';
    ctx.fillRect(0, 0, CHART_W, CHART_H);

    // Grid lines
    ctx.strokeStyle = '#f0f0f0';
    ctx.lineWidth = 1;
    const xTicks = 8;
    const yTicks = 6;
    for (let i = 0; i <= yTicks; i++) {
      const yVal = (yMax / yTicks) * i;
      const y = yScale(yVal);
      ctx.beginPath();
      ctx.moveTo(PAD.left, y);
      ctx.lineTo(CHART_W - PAD.right, y);
      ctx.stroke();
    }
    for (let i = 0; i <= xTicks; i++) {
      const xVal = (xMax / xTicks) * i;
      const x = xScale(xVal);
      ctx.beginPath();
      ctx.moveTo(x, PAD.top);
      ctx.lineTo(x, PAD.top + plotH);
      ctx.stroke();
    }

    // Axes
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(PAD.left, PAD.top);
    ctx.lineTo(PAD.left, PAD.top + plotH);
    ctx.lineTo(CHART_W - PAD.right, PAD.top + plotH);
    ctx.stroke();

    // Axis labels
    ctx.fillStyle = '#999';
    ctx.font = '11px Inter, sans-serif';
    ctx.textAlign = 'center';
    for (let i = 0; i <= xTicks; i++) {
      const xVal = Math.round((xMax / xTicks) * i);
      ctx.fillText(xVal.toString(), xScale(xVal), PAD.top + plotH + 20);
    }
    ctx.textAlign = 'right';
    for (let i = 0; i <= yTicks; i++) {
      const yVal = Math.round((yMax / yTicks) * i);
      ctx.fillText(yVal.toString(), PAD.left - 10, yScale(yVal) + 4);
    }

    // Axis titles
    ctx.fillStyle = '#636e72';
    ctx.font = '13px Inter, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Row Length (panels)', CHART_W / 2, CHART_H - 8);

    ctx.save();
    ctx.translate(16, CHART_H / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillText('Faults (Critical + High)', 0, 0);
    ctx.restore();

    // Regression line
    if (stats.slope !== undefined && points.length > 2) {
      ctx.strokeStyle = 'rgba(108, 92, 231, 0.5)';
      ctx.lineWidth = 2;
      ctx.setLineDash([6, 4]);
      const lineX1 = 0;
      const lineX2 = xMax;
      const lineY1 = stats.intercept;
      const lineY2 = stats.slope * xMax + stats.intercept;
      ctx.beginPath();
      ctx.moveTo(xScale(lineX1), yScale(Math.max(0, lineY1)));
      ctx.lineTo(xScale(lineX2), yScale(Math.max(0, lineY2)));
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Points
    for (const p of points) {
      const x = xScale(p.row_length);
      const y = yScale(p.total_faults);
      const isHovered = hoveredPoint && hoveredPoint.robot_number === p.robot_number;

      // Color by fault count
      let color;
      if (p.critical > 0) {
        color = 'rgba(192, 57, 43, 0.6)';
      } else if (p.high > 0) {
        color = 'rgba(230, 126, 34, 0.6)';
      } else {
        color = 'rgba(39, 174, 96, 0.6)';
      }

      ctx.beginPath();
      ctx.arc(x, y, isHovered ? 6 : 4, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      if (isHovered) {
        ctx.strokeStyle = '#2d3436';
        ctx.lineWidth = 2;
        ctx.stroke();
      }
    }
  }, [points, stats, xMax, yMax, xScale, yScale, hoveredPoint, plotW, plotH]);

  // Mouse interaction
  const handleMouseMove = (e) => {
    const canvas = canvasRef.current;
    if (!canvas || !points.length) return;
    const rect = canvas.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;

    let closest = null;
    let closestDist = Infinity;
    for (const p of points) {
      const x = xScale(p.row_length);
      const y = yScale(p.total_faults);
      const dist = Math.sqrt((mx - x) ** 2 + (my - y) ** 2);
      if (dist < 20 && dist < closestDist) {
        closest = p;
        closestDist = dist;
      }
    }

    if (closest) {
      setHoveredPoint(closest);
      setTooltip({
        show: true,
        x: e.clientX - (containerRef.current?.getBoundingClientRect()?.left || 0) + 12,
        y: e.clientY - (containerRef.current?.getBoundingClientRect()?.top || 0) - 10,
        data: closest,
      });
    } else {
      setHoveredPoint(null);
      setTooltip({ show: false, x: 0, y: 0, data: null });
    }
  };

  const correlationStrength = (r) => {
    const absR = Math.abs(r);
    if (absR >= 0.7) return { text: 'Strong', color: '#c0392b' };
    if (absR >= 0.4) return { text: 'Moderate', color: '#e17055' };
    if (absR >= 0.2) return { text: 'Weak', color: '#fdcb6e' };
    return { text: 'Very Weak / None', color: '#00b894' };
  };

  if (loading) return <div style={{ padding: 40, color: '#999' }}>Loading analysis...</div>;
  if (error) return <div style={{ padding: 40, color: '#c0392b' }}>Error loading data: {error.message}</div>;

  const strength = correlationStrength(stats.correlation_r || 0);

  return (
    <div>
      <h1 style={styles.title}>Analytics</h1>
      <p style={styles.subtitle}>
        Data-driven insights across the fleet
      </p>

      {/* Analysis: Row Length vs Faults */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div>
            <h2 style={styles.cardTitle}>Row Length vs Faults Correlation</h2>
            <p style={styles.cardDesc}>
              Do robots cleaning longer rows experience more faults? India fleet, last {days} days.
            </p>
          </div>
          <div style={styles.daysSelector}>
            {DAYS_OPTIONS.map(d => (
              <button
                key={d}
                onClick={() => setDays(d)}
                style={{
                  ...styles.dayBtn,
                  ...(d === days ? styles.dayBtnActive : {}),
                }}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        {/* Stats cards */}
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.total_robots?.toLocaleString() || 0}</div>
            <div style={styles.statLabel}>Robots Analyzed</div>
          </div>
          <div style={styles.statCard}>
            <div style={{
              ...styles.statValue,
              color: strength.color,
            }}>
              r = {stats.correlation_r || 0}
            </div>
            <div style={styles.statLabel}>Pearson Correlation</div>
          </div>
          <div style={styles.statCard}>
            <div style={{
              ...styles.statValue,
              color: strength.color,
            }}>
              {strength.text}
            </div>
            <div style={styles.statLabel}>Correlation Strength</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>R² = {stats.r_squared || 0}</div>
            <div style={styles.statLabel}>Variance Explained</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.mean_row_length || 0}</div>
            <div style={styles.statLabel}>Avg Row Length (panels)</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{stats.mean_faults || 0}</div>
            <div style={styles.statLabel}>Avg Faults / Robot</div>
          </div>
        </div>

        {/* Scatter plot */}
        <div ref={containerRef} style={styles.chartContainer}>
          {points.length > 0 ? (
            <>
              <canvas
                ref={canvasRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={() => {
                  setHoveredPoint(null);
                  setTooltip({ show: false, x: 0, y: 0, data: null });
                }}
                style={{ cursor: 'crosshair', borderRadius: 8 }}
              />
              {tooltip.show && tooltip.data && (
                <div style={{
                  ...styles.tooltip,
                  left: tooltip.x,
                  top: tooltip.y,
                }}>
                  <div style={styles.tooltipTitle}>Robot #{tooltip.data.robot_number}</div>
                  <div style={styles.tooltipRow}>{tooltip.data.project_name}</div>
                  <div style={styles.tooltipRow}>Row {tooltip.data.row_number} — {tooltip.data.row_length} panels</div>
                  <div style={styles.tooltipRow}>
                    <span style={{ color: '#c0392b' }}>{tooltip.data.critical} critical</span>
                    {' + '}
                    <span style={{ color: '#e17055' }}>{tooltip.data.high} high</span>
                    {' = '}
                    <strong>{tooltip.data.total_faults} faults</strong>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div style={{ padding: 60, textAlign: 'center', color: '#999' }}>
              No data available for the selected period.
            </div>
          )}
        </div>

        {/* Legend */}
        <div style={styles.legendBar}>
          <span style={{ ...styles.legendDot, background: 'rgba(192, 57, 43, 0.6)' }} />
          <span style={styles.legendText}>Has critical faults</span>
          <span style={{ ...styles.legendDot, background: 'rgba(230, 126, 34, 0.6)', marginLeft: 16 }} />
          <span style={styles.legendText}>High faults only</span>
          <span style={{ ...styles.legendDot, background: 'rgba(39, 174, 96, 0.6)', marginLeft: 16 }} />
          <span style={styles.legendText}>No faults</span>
          <span style={{ ...styles.legendLine, marginLeft: 16 }} />
          <span style={styles.legendText}>Regression line (trend)</span>
        </div>

        {/* Interpretation */}
        {points.length > 0 && (
          <div style={styles.interpretation}>
            <strong>Interpretation:</strong>{' '}
            {Math.abs(stats.correlation_r) >= 0.4 ? (
              <>
                There is a <span style={{ color: strength.color, fontWeight: 700 }}>{strength.text.toLowerCase()}</span> {stats.correlation_r > 0 ? 'positive' : 'negative'} correlation
                (r={stats.correlation_r}) between row length and fault count.
                {stats.correlation_r > 0
                  ? ' Robots on longer rows tend to experience more faults.'
                  : ' Robots on longer rows tend to experience fewer faults.'}
                {' '}For every additional 100 panels, faults {stats.slope > 0 ? 'increase' : 'decrease'} by ~{Math.abs(stats.slope * 100).toFixed(1)}.
              </>
            ) : (
              <>
                The correlation is <span style={{ color: strength.color, fontWeight: 700 }}>{strength.text.toLowerCase()}</span> (r={stats.correlation_r}).
                Row length does not appear to be a significant predictor of fault frequency.
                Other factors (robot age, maintenance history, environment) may play a larger role.
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  title: { fontSize: 24, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 14, color: '#636e72', marginTop: 4, marginBottom: 24 },

  card: {
    background: '#fff',
    borderRadius: 12,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    padding: 24,
    marginBottom: 24,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
    flexWrap: 'wrap',
    gap: 12,
  },
  cardTitle: { fontSize: 18, fontWeight: 700, margin: 0 },
  cardDesc: { fontSize: 13, color: '#636e72', marginTop: 4, margin: 0 },

  daysSelector: { display: 'flex', gap: 4 },
  dayBtn: {
    padding: '6px 14px',
    borderRadius: 8,
    border: '1px solid #ddd',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 500,
    color: '#636e72',
  },
  dayBtnActive: {
    background: '#6c5ce7',
    color: '#fff',
    borderColor: '#6c5ce7',
  },

  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 12,
    marginBottom: 20,
  },
  statCard: {
    padding: '14px 16px',
    background: '#f8f9fa',
    borderRadius: 10,
    textAlign: 'center',
  },
  statValue: { fontSize: 18, fontWeight: 700, color: '#2d3436' },
  statLabel: { fontSize: 11, color: '#999', marginTop: 4, textTransform: 'uppercase' },

  chartContainer: {
    position: 'relative',
    background: '#fff',
    borderRadius: 8,
    border: '1px solid #eee',
    overflow: 'hidden',
    display: 'inline-block',
  },

  tooltip: {
    position: 'absolute',
    background: '#2d3436',
    color: '#fff',
    padding: '10px 14px',
    borderRadius: 8,
    fontSize: 12,
    pointerEvents: 'none',
    zIndex: 10,
    minWidth: 180,
    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
  },
  tooltipTitle: { fontWeight: 700, fontSize: 13, marginBottom: 4 },
  tooltipRow: { marginTop: 2, lineHeight: 1.4 },

  legendBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginTop: 12,
    padding: '8px 0',
    fontSize: 12,
    color: '#636e72',
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    display: 'inline-block',
  },
  legendLine: {
    width: 24,
    height: 0,
    borderTop: '2px dashed rgba(108, 92, 231, 0.6)',
    display: 'inline-block',
  },
  legendText: { fontSize: 11 },

  interpretation: {
    marginTop: 16,
    padding: '14px 18px',
    background: '#f8f9fa',
    borderRadius: 10,
    fontSize: 13,
    lineHeight: 1.6,
    color: '#2d3436',
    borderLeft: '4px solid #6c5ce7',
  },
};
